// src/stores/app/uiStore.ts

import { ContactData, ListOption, Message } from '@/mocks/app/mockChatData';
import { ResponseTemplate } from '@/services/templates/templates.service';
import { defineStore } from 'pinia';
import { nextTick } from 'vue';
import { ResponseBot } from '../../services/bots/bots.service';
import { ResponseUsuario } from '@/services/usuarios/usuarios.service';
import { ResponseInbox } from '@/services/inbox/inbox.service';
import { ResponseCanal } from '@/services/canais/canais.service';
import { CampanhaImportacaoMensagem } from '@/services/campanhas/campanhas.service';
import { IdCampanha } from '@/services/common-types';
import { StorageService, MAX_FILE_SIZE_MB } from '../admin/storage';
import { toast } from 'vue3-toastify';
import { useAuthStoreUser } from './authUser';
import { normalizeUploadFile } from '@/utils/normalizeUploadFile';

// Debounce para evitar múltiplas requisições ao alternar o tema rapidamente.
let themePersistTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Abas da Central de Relatórios. A união vivia repetida em quatro assinaturas — acrescentar
 * uma aba exigia lembrar das quatro, e esquecer uma só aparecia como erro de tipo no ponto
 * de chamada, não aqui.
 */
export type ReportTabKey =
    | 'general'
    | 'agente-ia'
    | 'desempenho'
    | 'rotation'
    | 'contacts'
    | 'conversoes'
    | 'chat-interno';

export type ResponseUsuarioWithVinculos = (ResponseUsuario & { inboxes: ResponseInbox[], canais: ResponseCanal[], senha?: string, showPass: boolean, active: boolean, avatar?: string, permissoes: Record<string, boolean>, avatarFile?: File });

export interface PreviewItem {
    file: File;
    caption: string;
}

interface ListMessageData {
    title: string;
    options: ListOption[];
    messageId?: string;
}

export type ViewMode = 'list'|'kanban';
export type KanbanViewMode = 'quadros'|'canais';

interface UiState {
    theme: 'light'|'dark';

    // Kanban
    currentKanbanViewMode: KanbanViewMode;
    defaultKanbanViewMode: KanbanViewMode;
    isShowingKanbanDock: boolean;

    activeTab: string;
    activeListTab: string;

    isAddTagModalOpen: boolean;
    isLinkTagModalOpen: boolean;
    isForwardModalOpen: boolean;
    messageIdToScrollTo: string | null;

    isPinModalOpen: boolean;
    messageToPin: Message | null;
    isAiChatModalOpen: boolean;
    isHistoryModalOpen: boolean;

    isTransferModalOpen: boolean;
    isBotTriggerModalOpen: boolean;
    isBotListModalOpen: boolean;
    isBotEditorModalOpen: boolean;
    shouldReopenBotList: boolean;
    botToEdit: ResponseBot | null;
    isPollModalOpen: boolean;

    isSendContactModalOpen: boolean;
    isTemplatesModalOpen: boolean;

    isFilePreviewModalOpen: boolean;
    previewItems: PreviewItem[];
    activePreviewIndex: number;

    isMediaViewerOpen: boolean;
    mediaToView: { 
        url: string, 
        type: 'image' | 'video', 
        caption?: string,
        gallery?: { url: string, type: 'image' | 'video', caption?: string }[],
        galleryIndex?: number
    } | null;

    isListMessageModalOpen: boolean;
    listMessageData: ListMessageData | null;

    isInboxModalOpen: boolean;
    isWhatsappSetupModalOpen: boolean;
    isContactsModalOpen: boolean;

    isAgenteIaModalOpen: boolean;
    agenteIaModalInboxId: string | null;
    isCloneUpsellModalOpen: boolean;

    isAgentsModalOpen: boolean;

    /**
     * Modal de Jornada de Trabalho. `jornadaTrabalhoFoco` é o alvo do deep-link
     * vindo dos avisos "atendente sem Jornada": o modal abre já no atendente e no
     * inbox certos e então limpa o foco, para que reabrir pelo menu caia no estado
     * neutro. Mesmo desenho de `campanhaFoco`.
     */
    isJornadaTrabalhoModalOpen: boolean;
    jornadaTrabalhoFoco: { idUsuario: string | null; idInbox: string | null } | null;

    isAgentAnalyticsModalOpen: boolean;
    agentToAnalyze: ResponseUsuarioWithVinculos | null;

    isDashboardModalOpen: boolean;

    isNewsModalOpen: boolean;

    isConfirmTemplateModalOpen: boolean;
    templateToConfirm: ResponseTemplate | null;

    isGroupBanModalOpen: boolean;
    isAttendanceReportModalOpen: boolean;

    isReportsModalOpen: boolean;
    activeReportTab: ReportTabKey;
    /**
     * Filtro inicial do relatório de rodízio, quando ele é aberto de fora (ícone de
     * alerta do canal no modal de inbox). A aba consome e limpa.
     */
    rodizioReportPreset: { idInbox: string; idCanal: string | null } | null;
    historyModalContext: { idContato: string; idInbox: string } | null;

    isShowingQrCode: boolean;
    selectedInboxId: string|null;

    isChatModalOpen: boolean;

    isContactListModalOpen: boolean;
    contactListToView: ContactData[];

    contactListModalData: ContactData[];

    isUserProfileModalOpen: boolean;
    isBatchActionModalOpen: boolean;
    isCampanhasModalOpen: boolean;

    isImportContactsModalOpen: boolean;
    isContactsOperationsHistoryModalOpen: boolean;
    /** Importação aberta no modal de conferência (task 104807); nulo = fechado. */
    importacaoConferenciaId: string | null;
    isExportacoesModalOpen: boolean;
    /** Exportação vinda de uma notificação: o modal confere se ela é da conta atual. */
    exportacaoEmFoco: string | null;

    isGerarOportunidadeModalOpen: boolean;
    isOportunidadesAxionModalOpen: boolean;
    isVincularAxionModalOpen: boolean;
    isAbrirChamadoAxionModalOpen: boolean;
    isChamadosContatoAxionModalOpen: boolean;
    isDispararProcessoAxionModalOpen: boolean;
    isProcessosContatoAxionModalOpen: boolean;

    isAllNotificationsModalOpen: boolean;

    isBancoConhecimentoModalOpen: boolean;

    storageService: StorageService;

    contactsOperationsHistoryInitialTab: 'planilha' | 'whatsapp';
    /** Importação cujo resultado o histórico abre direto (ex.: logo após confirmar a conferência). */
    contactsOperationsHistoryImportacaoFoco: string | null;

    /**
     * Contexto de campanha do modal de importação. Quando presente, a planilha é enviada
     * para `POST /disparos/:id/importacoes` (cada linha válida vira um envio da campanha)
     * em vez da importação solta de contatos. `null` = fluxo original.
     */
    importContactsCampanhaContext: ImportContactsCampanhaContext | null;
    campanhaFoco: { idDisparo: string; idAgendamento: string | null } | null;
    campanhasModalMontado: boolean;
}

export type ImportContactsCampanhaContext = {
    idCampanha: IdCampanha;
    /** Conteúdo já configurado na campanha, congelado na importação. */
    mensagem: CampanhaImportacaoMensagem;
};

export type UiStore = ReturnType<typeof useUiStore>
export const useUiStore = defineStore('ui', {
    state: (): UiState => ({
        theme: (localStorage.getItem('user-theme') as 'light'|'dark') || 'light',

        currentKanbanViewMode: 'quadros',
        defaultKanbanViewMode: 'quadros',
        isShowingKanbanDock: true,

        activeTab: 'Home',

        isAddTagModalOpen: false,
        isLinkTagModalOpen: false,
        isForwardModalOpen: false,
        messageIdToScrollTo: null,
        isPinModalOpen: false,
        messageToPin: null,
        isAiChatModalOpen: false,
        isHistoryModalOpen: false,
        isTransferModalOpen: false,
        isBotTriggerModalOpen: false,
        isBotListModalOpen: false,
        isBotEditorModalOpen: false,
        shouldReopenBotList: false,
        botToEdit: null,
        isPollModalOpen: false,
        isSendContactModalOpen: false,
        isTemplatesModalOpen: false,

        isFilePreviewModalOpen: false,
        previewItems: [],
        activePreviewIndex: 0,

        isMediaViewerOpen: false,
        mediaToView: null,

        isListMessageModalOpen: false,
        listMessageData: null,

        isInboxModalOpen: false,
        isWhatsappSetupModalOpen: false,
        isContactsModalOpen: false,

        isAgenteIaModalOpen: false,
        agenteIaModalInboxId: null,
        isCloneUpsellModalOpen: false,

        isAgentsModalOpen: false,

        isJornadaTrabalhoModalOpen: false,
        jornadaTrabalhoFoco: null,
        isAgentAnalyticsModalOpen: false,
        agentToAnalyze: null,

        isDashboardModalOpen: false,

        isNewsModalOpen: false,

        isConfirmTemplateModalOpen: false,
        templateToConfirm: null,

        isGroupBanModalOpen: false,
        isAttendanceReportModalOpen: false,

        isReportsModalOpen: false,
        activeReportTab: 'general' as ReportTabKey,
        rodizioReportPreset: null as { idInbox: string; idCanal: string | null } | null,
        historyModalContext: null as { idContato: string; idInbox: string } | null,

        isShowingQrCode: false,
        selectedInboxId: null,

        isChatModalOpen: false,
        activeListTab: 'tudo',

        isContactListModalOpen: false,
        contactListToView: [],

        contactListModalData: [],

        isUserProfileModalOpen: false,
        isBatchActionModalOpen: false,
        isCampanhasModalOpen: false,
        
        isImportContactsModalOpen: false,
        isContactsOperationsHistoryModalOpen: false,
        importacaoConferenciaId: null as string | null,
        isExportacoesModalOpen: false,
        exportacaoEmFoco: null,

        isGerarOportunidadeModalOpen: false,
        isOportunidadesAxionModalOpen: false,
        isVincularAxionModalOpen: false,
        isAbrirChamadoAxionModalOpen: false,
        isChamadosContatoAxionModalOpen: false,
        isDispararProcessoAxionModalOpen: false,
        isProcessosContatoAxionModalOpen: false,

        isAllNotificationsModalOpen: false,

        isBancoConhecimentoModalOpen: false,

        storageService: new StorageService(),

        contactsOperationsHistoryInitialTab: 'planilha' as 'planilha' | 'whatsapp',
        contactsOperationsHistoryImportacaoFoco: null as string | null,
        importContactsCampanhaContext: null as ImportContactsCampanhaContext | null,

        /**
         * Campanha (e envio) que o modal de Campanhas deve focar ao abrir. Vem da
         * anotação interna clicável do chat.
         *
         * Efêmero de propósito e fora do `persist`: é uma intenção de navegação de
         * um clique, não preferência. Persistido, o modal saltaria para um envio
         * antigo em toda reabertura.
         */
        campanhaFoco: null as { idDisparo: string; idAgendamento: string | null } | null,

        /**
         * O modal de Campanhas está montado nesta tela?
         *
         * `PainelPage` o monta; `ChatEmbedPage` não — e o embed renderiza o chat,
         * anotações internas incluídas. Sem esta bandeira, a anotação de campanha
         * ficaria clicável lá e o clique não abriria nada. Quem responde é o próprio
         * modal, no ciclo de vida dele: perguntar pela rota erraria na próxima tela
         * que montar (ou deixar de montar) o modal.
         */
        campanhasModalMontado: false,
    }),
    actions: {
        openBancoConhecimentoModal() {
            this.isBancoConhecimentoModalOpen = true;
        },
        closeBancoConhecimentoModal() {
            this.isBancoConhecimentoModalOpen = false;
        },

        openAddTagModal() {
            this.isAddTagModalOpen = true;
        },
        closeAddTagModal() {
            this.isAddTagModalOpen = false;
        },
        openLinkTagModal() {
            this.isLinkTagModalOpen = true;
        },
        closeLinkTagModal() {
            this.isLinkTagModalOpen = false;
        },

        openForwardModal() {
            this.isForwardModalOpen = true;
        },
        closeForwardModal() {
            this.isForwardModalOpen = false;
        },
        scrollToMessage(messageId: string) {
            this.messageIdToScrollTo = messageId;
        },

        clearScrollTo() {
            this.messageIdToScrollTo = null;
        },

        openPinModal(message: Message) {
            this.messageToPin = message; 
            this.isPinModalOpen = true;
        },
        closePinModal() {
            this.isPinModalOpen = false;
            this.messageToPin = null;
        },

        openAiChatModal() {
            this.isAiChatModalOpen = true;
        },
        closeAiChatModal() {
            this.isAiChatModalOpen = false;
        },

        openHistoryModal(context?: { idContato: string; idInbox: string }) {
            this.historyModalContext = context ?? null;
            this.isHistoryModalOpen = true;
        },
        closeHistoryModal() {
            this.isHistoryModalOpen = false;
            this.historyModalContext = null;
        },

        openTransferModal() {
            this.isTransferModalOpen = true;
        },
        closeTransferModal() {
            this.isTransferModalOpen = false;
        },

        openBotTriggerModal() {
            this.isBotTriggerModalOpen = true;
        },
        closeBotTriggerModal() {
            this.isBotTriggerModalOpen = false;
        },

        openGerarOportunidadeModal() {
            this.isGerarOportunidadeModalOpen = true;
        },
        closeGerarOportunidadeModal() {
            this.isGerarOportunidadeModalOpen = false;
        },

        openOportunidadesAxionModal() {
            this.isOportunidadesAxionModalOpen = true;
        },
        closeOportunidadesAxionModal() {
            this.isOportunidadesAxionModalOpen = false;
        },

        openVincularAxionModal() {
            this.isVincularAxionModalOpen = true;
        },
        closeVincularAxionModal() {
            this.isVincularAxionModalOpen = false;
        },

        openAbrirChamadoAxionModal() {
            this.isAbrirChamadoAxionModalOpen = true;
        },
        closeAbrirChamadoAxionModal() {
            this.isAbrirChamadoAxionModalOpen = false;
        },

        openChamadosContatoAxionModal() {
            this.isChamadosContatoAxionModalOpen = true;
        },
        closeChamadosContatoAxionModal() {
            this.isChamadosContatoAxionModalOpen = false;
        },

        openDispararProcessoAxionModal() {
            this.isDispararProcessoAxionModalOpen = true;
        },
        closeDispararProcessoAxionModal() {
            this.isDispararProcessoAxionModalOpen = false;
        },

        openProcessosContatoAxionModal() {
            this.isProcessosContatoAxionModalOpen = true;
        },
        closeProcessosContatoAxionModal() {
            this.isProcessosContatoAxionModalOpen = false;
        },

        openBotListModal() {
            this.isBotListModalOpen = true;
        },
        closeBotListModal() {
            this.isBotListModalOpen = false;
        },

        openPollModal() {
            this.isPollModalOpen = true;
        },
        closePollModal() {
            this.isPollModalOpen = false;
        },

        openSendContactModal() {
            this.isSendContactModalOpen = true;
        },
        closeSendContactModal() {
            this.isSendContactModalOpen = false;
        },

        openTemplatesModal() {
            if (!this.isTemplatesModalOpen) return this.isTemplatesModalOpen = true;

            this.isTemplatesModalOpen = false
            void nextTick(() => this.isTemplatesModalOpen = true);
        },
        closeTemplatesModal() {
            this.isTemplatesModalOpen = false;
        },

        openFilePreviewModal(files: File|File[]) {
            if (!Array.isArray(files)) files = [files];
            const jaEstavaAberto = this.isFilePreviewModalOpen;
            const sucesso = this.addFileToPreview(files);

            // Abertura nova: mostra o primeiro arquivo. Modal já aberto (ex.: Ctrl+V de um print):
            // mantém o índice que `addFileToPreview` apontou para o arquivo recém-adicionado.
            if (!jaEstavaAberto) this.activePreviewIndex = 0;

            // Falha de validação com o modal já aberto não pode fechá-lo e descartar os anexos.
            this.isFilePreviewModalOpen = jaEstavaAberto || sucesso;
        },

        addFileToPreview(files: File[]): boolean {
            for (const file of files) {
                if (!this.storageService.isFileSizeAllowed(file)){
                    toast.error(`O arquivo "${file.name}" excede o tamanho máximo permitido de ${MAX_FILE_SIZE_MB} MB.`);
                    return false;
                }
            }

            for (const file of files) {
                // Apelidos de JPEG (.jfif, .jpe...) sobem ao S3 com o nome original e o provedor
                // recusa pela extensão da URL — normaliza antes de entrar na pré-visualização.
                this.previewItems.push({ file: normalizeUploadFile(file), caption: '' });
                this.activePreviewIndex = this.previewItems.length - 1; 
            }
            return true;
        },

        replaceFileInPreview(index: number, file: File) {
            const item = this.previewItems[index];
            if (item) {
                item.file = file;
            }
        },

        removeFileFromPreview(index: number) {
            this.previewItems.splice(index, 1);
            if (this.previewItems.length === 0) {
                this.closeFilePreviewModal();
            } else if (this.activePreviewIndex >= this.previewItems.length) {
                this.activePreviewIndex = this.previewItems.length - 1;
            }
        },

        setActivePreviewIndex(index: number) {
            this.activePreviewIndex = index;
        },

        closeFilePreviewModal() {
            this.isFilePreviewModalOpen = false;
            this.previewItems = [];
            this.activePreviewIndex = 0;
        },

        // DEPOIS
        openMediaViewer(
            url: string,
            type: 'image' | 'video' = 'image',
            caption = '',
            gallery?: { url: string, type: 'image' | 'video', caption?: string }[],
            galleryIndex?: number
        ) {
            this.mediaToView = { url, type, caption, gallery, galleryIndex };
            this.isMediaViewerOpen = true;
        },
        closeMediaViewer() {
            this.isMediaViewerOpen = false;
            this.mediaToView = null;
        },

        openListMessageModal(data: ListMessageData) {
            this.listMessageData = data;
            this.isListMessageModalOpen = true;
        },
        closeListMessageModal() {
            this.isListMessageModalOpen = false;
            setTimeout(() => { this.listMessageData = null; }, 200);
        },

        openInboxModal() {
            this.isInboxModalOpen = true;
        },
        closeInboxModal() {
            this.isInboxModalOpen = false;
        },

        openWhatsappSetupModal() {
            this.isWhatsappSetupModalOpen = true;
        },
        closeWhatsappSetupModal() {
            this.isWhatsappSetupModalOpen = false;
        },

        setBotToEdit(bot: ResponseBot) {
          this.botToEdit = bot;
        },

        openBotEditorModal(bot: ResponseBot, fromBotList: boolean = true) {
            this.setBotToEdit(bot);
            this.isBotEditorModalOpen = true;
            this.isBotListModalOpen = false;
            this.shouldReopenBotList = fromBotList;
        },
        closeBotEditorModal() {
            this.isBotEditorModalOpen = false;
            this.botToEdit = null;
            if (this.shouldReopenBotList) {
                this.isBotListModalOpen = true;
            }
        },

        openContactsModal() {
            this.isContactsModalOpen = true;
        },
        closeContactsModal() {
            this.isContactsModalOpen = false;
        },

        openAgenteIaModal(idInbox: string) {
            this.agenteIaModalInboxId = idInbox;
            this.isAgenteIaModalOpen = true;
        },
        closeAgenteIaModal() {
            this.isAgenteIaModalOpen = false;
        },

        openCloneUpsellModal() {
            this.isCloneUpsellModalOpen = true;
        },
        closeCloneUpsellModal() {
            this.isCloneUpsellModalOpen = false;
        },
        changeTheme(){
            this.setTheme(this.theme === 'light' ? 'dark' : 'light')
        },
        // Define o tema. persist=true grava a preferência no backend (toggles do usuário).
        // Use persist=false ao apenas refletir um valor já vindo do servidor/sistema.
        setTheme(theme: 'light' | 'dark', persist = true){
            this.theme = theme
            this.updateTheme()
            if (persist) this.persistThemePreference(theme)
        },
        persistThemePreference(theme: 'light' | 'dark'){
            const authStore = useAuthStoreUser()
            const userId = authStore.user?.id
            if (!authStore.isLoggedIn || !userId) return

            if (themePersistTimer !== null) clearTimeout(themePersistTimer)
            themePersistTimer = setTimeout(() => {
                themePersistTimer = null
                void authStore.usuarioService.update({ id: userId, tema: theme }).catch((e) => {
                    console.warn('[uiStore] Falha ao persistir o tema no backend:', e)
                })
            }, 500)
        },
        updateTheme(){
            window.document.querySelector('body')?.setAttribute('data-bs-theme', this.theme)
        },

        setActiveTab(tabName: string) {
            this.activeTab = tabName;

            this.isDashboardModalOpen = tabName === "Dashboard";

            if (
            ["Home", "Lista", "Kanban", "Dashboard", "Novidades", "ChatEquipe", "Radar"].includes(
                tabName,
            )
            ) {
            this.closeReportsModal();
            }
        },

        openAgentsModal() {
          this.isAgentsModalOpen = true;
        },
        closeAgentsModal() {
          this.isAgentsModalOpen = false;
        },

        /**
         * `foco` abre o modal de Jornada já no atendente/inbox indicados — é o
         * caminho dos avisos "atendente sem Jornada de Trabalho" no form do canal e
         * no dropdown de atribuição. Sem `foco`, abre no estado neutro.
         */
        openJornadaTrabalhoModal(foco: { idUsuario?: string | null; idInbox?: string | null } | null = null) {
          this.jornadaTrabalhoFoco = foco ? { idUsuario: foco.idUsuario ?? null, idInbox: foco.idInbox ?? null } : null;
          this.isJornadaTrabalhoModalOpen = true;
        },
        closeJornadaTrabalhoModal() {
          this.isJornadaTrabalhoModalOpen = false;
          this.jornadaTrabalhoFoco = null;
        },
        /** O modal avisa que já consumiu o alvo, para o watcher não reagir de novo. */
        limparJornadaTrabalhoFoco() {
          this.jornadaTrabalhoFoco = null;
        },

        openAgentAnalyticsModal(agent: ResponseUsuarioWithVinculos) {
          this.agentToAnalyze = agent;
          this.isAgentAnalyticsModalOpen = true;
        },
        closeAgentAnalyticsModal() {
          this.isAgentAnalyticsModalOpen = false;
          this.agentToAnalyze = null;
        },

        openDashboardModal() {
          this.isDashboardModalOpen = true;
          this.setActiveTab("Dashboard");
        },
        closeDashboardModal() {
          this.isDashboardModalOpen = false;
          this.setActiveTab("Lista");
        },

        openNewsModal() {
            this.isNewsModalOpen = true;
        },
        closeNewsModal() {
            this.isNewsModalOpen = false;
        },

        openAllNotificationsModal() {
            this.isAllNotificationsModalOpen = true;
        },
        closeAllNotificationsModal() {
            this.isAllNotificationsModalOpen = false;
        },

        openConfirmTemplateModal(template: ResponseTemplate) {
            this.templateToConfirm = template;
            this.isConfirmTemplateModalOpen = true;
        },
        closeConfirmTemplateModal() {
            this.isConfirmTemplateModalOpen = false;
            this.templateToConfirm = null;
        },

        openGroupBanModal() {
            this.isGroupBanModalOpen = true;
        },
        closeGroupBanModal() {
            this.isGroupBanModalOpen = false;
        },

        openAttendanceReportModal() {
            this.isAttendanceReportModalOpen = true;
        },
        closeAttendanceReportModal() {
            this.isAttendanceReportModalOpen = false;
        },

        openChatModal() {
            this.isChatModalOpen = true;
        },
        closeChatModal() {
            this.isChatModalOpen = false;
        },

        setActiveListTab(tab: string) {
            this.activeListTab = tab;
        },

        openContactListModal(contacts: ContactData[]) {
            this.contactListToView = contacts;
            this.contactListModalData = JSON.parse(JSON.stringify(contacts)) as ContactData[];
            this.isContactListModalOpen = true;
        },
        closeContactListModal() {
            this.isContactListModalOpen = false;
            this.contactListToView = [];
            setTimeout(() => {
                this.contactListModalData = [];
            }, 300);
        },

        openUserProfileModal() {
            this.isUserProfileModalOpen = true;
        },
        closeUserProfileModal() {
            this.isUserProfileModalOpen = false;
        },

        openBatchActionModal() {
            this.isBatchActionModalOpen = true;
        },
        closeBatchActionModal() {
            this.isBatchActionModalOpen = false;
        },

        /**
         * `foco` abre o modal já numa campanha e, se informado, rolando até o envio
         * dela. É o caminho da anotação interna clicável; sem `foco`, abre na lista
         * como sempre.
         */
        openCampanhasModal(foco: { idDisparo: string; idAgendamento?: string | null } | null = null) {
            this.campanhaFoco = foco ? { idDisparo: foco.idDisparo, idAgendamento: foco.idAgendamento ?? null } : null;
            this.isCampanhasModalOpen = true;
        },
        closeCampanhasModal() {
            this.isCampanhasModalOpen = false;
            // Limpa o alvo junto com o fechamento: reabrir pelo menu tem que cair na
            // lista, não repetir o salto do último clique na anotação.
            this.campanhaFoco = null;
        },
        registrarCampanhasModal(montado: boolean) {
            this.campanhasModalMontado = montado;
        },
        /** O modal avisa que já consumiu o alvo, para o watcher não reagir de novo. */
        limparCampanhaFoco() {
            this.campanhaFoco = null;
        },

        /**
         * `campanhaContext` liga a importação a uma campanha: mesma planilha, mesmo wizard,
         * mas o destino é a rota da campanha e cada linha válida vira um envio.
         */
        openImportContactsModal(campanhaContext: ImportContactsCampanhaContext | null = null) {
            this.importContactsCampanhaContext = campanhaContext;
            this.isImportContactsModalOpen = true;
        },
        closeImportContactsModal() {
            this.isImportContactsModalOpen = false;
            this.importContactsCampanhaContext = null;
        },

        openContactsOperationsHistoryModal(tab: 'planilha' | 'whatsapp' = 'planilha', importacaoFoco: string | null = null) {
            this.contactsOperationsHistoryInitialTab = tab;
            this.contactsOperationsHistoryImportacaoFoco = importacaoFoco;
            this.isContactsOperationsHistoryModalOpen = true;
        },
        closeContactsOperationsHistoryModal() {
            this.isContactsOperationsHistoryModalOpen = false;
        },

        openImportReviewModal(idImportacao: string) {
            this.importacaoConferenciaId = idImportacao;
        },
        closeImportReviewModal() {
            this.importacaoConferenciaId = null;
        },
        openExportacoesModal(idExportacaoEmFoco: string | null = null) {
            this.exportacaoEmFoco = idExportacaoEmFoco;
            this.isExportacoesModalOpen = true;
        },
        closeExportacoesModal() {
            this.isExportacoesModalOpen = false;
        },

        openReportsModal(tab: ReportTabKey = 'general') {
            this.activeReportTab = tab;
            this.isReportsModalOpen = true;
        },

        closeReportsModal() {
            this.isReportsModalOpen = false;
        },

        /** Abre o relatório de rodízio já filtrado num inbox (e canal). */
        openRodizioReport(idInbox: string, idCanal: string | null = null) {
            this.rodizioReportPreset = { idInbox, idCanal };
            this.openReportsModal('rotation');
        },

        setReportTab(tab: ReportTabKey) {
            this.activeReportTab = tab;
        }
    },
    persist: {
        pick: ['theme', 'defaultKanbanViewMode']
    }
});